import { isMobileDevice } from './isMobileDevice';

export type MessagingChannel = 'telegram' | 'bale';

function parseBotWebDeepLink(webUrl: string): { bot: string; start?: string } | null {
  try {
    const url = new URL(webUrl);
    const host = url.hostname.toLowerCase();
    if (host !== 't.me' && host !== 'ble.ir') {
      return null;
    }
    const bot = url.pathname.replace(/^\//, '').split('/').filter(Boolean)[0];
    if (!bot) {
      return null;
    }
    const start = url.searchParams.get('start') ?? undefined;
    return { bot, start };
  } catch {
    return null;
  }
}

function buildTelegramAppUrl(bot: string, start?: string): string {
  const params = new URLSearchParams({ domain: bot });
  if (start) {
    params.set('start', start);
  }
  return `tg://resolve?${params.toString()}`;
}

function buildTelegramAndroidIntentUrl(bot: string, start?: string): string {
  const params = new URLSearchParams({ domain: bot });
  if (start) {
    params.set('start', start);
  }
  return `intent://resolve?${params.toString()}#Intent;scheme=tg;package=org.telegram.messenger;end`;
}

function buildBaleAndroidIntentUrl(webUrl: string): string {
  const url = new URL(webUrl);
  const pathAndQuery = `${url.host}${url.pathname}${url.search}`;
  return `intent://${pathAndQuery}#Intent;scheme=https;package=ir.nasim;end`;
}

function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent);
}

function openInNewTab(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

/** Returns true when the OS likely switched to another app (Telegram/Bale). */
function tryOpenUrl(url: string, timeoutMs = 1600): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (opened: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener('blur', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(timer);
      resolve(opened);
    };
    const onLeave = () => finish(true);
    const onVisibility = () => {
      if (document.hidden) {
        finish(true);
      }
    };

    window.addEventListener('blur', onLeave);
    document.addEventListener('visibilitychange', onVisibility);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    const timer = window.setTimeout(() => finish(false), timeoutMs);
  });
}

/**
 * Open a Telegram/Bale bot deep link without leaving the current page.
 * On mobile, tries the native app first; falls back to https in a new tab.
 */
export async function openChannelBotDeepLink(webUrl: string, channel: MessagingChannel): Promise<void> {
  const parsed = parseBotWebDeepLink(webUrl);

  if (channel === 'telegram' && parsed && isMobileDevice()) {
    const appUrl = isAndroid()
      ? buildTelegramAndroidIntentUrl(parsed.bot, parsed.start)
      : buildTelegramAppUrl(parsed.bot, parsed.start);
    const openedInApp = await tryOpenUrl(appUrl);
    if (!openedInApp) {
      openInNewTab(webUrl);
    }
    return;
  }

  if (channel === 'bale' && isAndroid()) {
    const openedInApp = await tryOpenUrl(buildBaleAndroidIntentUrl(webUrl));
    if (!openedInApp) {
      openInNewTab(webUrl);
    }
    return;
  }

  // iOS Bale + desktop: https universal / web link in a new tab (Persianist stays open).
  openInNewTab(webUrl);
}
